import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Sparkles, ChevronRight, ChevronLeft, SkipForward, CheckCircle2, Loader2,
  Wrench, Calendar, ShoppingBag, PartyPopper, Settings2, ArrowRight,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { Sparkles, Wrench, Calendar, ShoppingBag, PartyPopper };

/**
 * Phase F.5 Batch 3 — Onboarding Wizard.
 *
 * 4-step flow:
 *   Step 1: welcome + skip button
 *   Step 2: business category (free text) + key-concept multi-select
 *   Step 3: pick a starter template (filtered to vertical_tags/concept_tags that match)
 *   Step 4: confirmation
 *
 * Auto-triggers on first login if onboarding_state.completed === false.
 * Always closeable. User can re-open from Settings later.
 */
export default function OnboardingWizard({ open, onClose, forceOpen = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "office_manager" || user?.role === "field_supervisor";

  const [step, setStep] = useState(1);
  const [category, setCategory] = useState("");
  const [concepts, setConcepts] = useState([]);
  const [allConcepts, setAllConcepts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [pickedTemplate, setPickedTemplate] = useState(null);
  const [installing, setInstalling] = useState(false);
  const [busy, setBusy] = useState(false);
  // iter148 — "Adjust before installing" preview mode
  const [previewMode, setPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState(null); // { job_statuses:[], custom_fields:[] }
  const [selectedStatuses, setSelectedStatuses] = useState(new Set());
  const [selectedFields, setSelectedFields] = useState(new Set());

  useEffect(() => {
    if (open && isAdmin) {
      axios.get(`${API}/onboarding/concepts`).then((r) => setAllConcepts(r.data?.concepts || [])).catch(() => {});
      axios.get(`${API}/platform/templates?only_published=true`).then((r) => setTemplates(r.data || [])).catch(() => {});
    }
  }, [open, isAdmin]);

  const matchedTemplates = useMemo(() => {
    // Score each template by overlap between selected concepts/category and the template tags.
    const catLow = (category || "").toLowerCase();
    return templates.map((t) => {
      let score = 0;
      for (const c of concepts) if ((t.concept_tags || []).includes(c)) score += 2;
      for (const v of (t.vertical_tags || [])) if (catLow && catLow.includes(v.toLowerCase())) score += 3;
      return { ...t, _score: score };
    }).sort((a, b) => b._score - a._score);
  }, [templates, concepts, category]);

  const toggleConcept = (key) => {
    setConcepts((curr) => curr.includes(key) ? curr.filter((k) => k !== key) : [...curr, key]);
  };

  const handleSkip = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/onboarding/state`, { skipped: true });
      toast.success("Onboarding skipped. You can revisit it anytime from Settings.");
      onClose?.();
    } catch {
      toast.error("Failed to save onboarding state");
    } finally { setBusy(false); }
  };

  const handleFinishWithoutInstall = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/onboarding/state`, {
        business_category: category,
        key_concepts: concepts,
        completed: true,
      });
      toast.success("Onboarding complete!");
      onClose?.();
    } catch {
      toast.error("Failed to save onboarding");
    } finally { setBusy(false); }
  };

  const handleInstall = async () => {
    if (!pickedTemplate) return;
    setInstalling(true);
    try {
      const body = previewMode ? {
        selected_status_names: Array.from(selectedStatuses),
        selected_field_keys: Array.from(selectedFields),
      } : {};
      const r = await axios.post(`${API}/platform/templates/${pickedTemplate.id}/install`, body);
      await axios.put(`${API}/onboarding/state`, {
        business_category: category,
        key_concepts: concepts,
        installed_template_id: pickedTemplate.id,
        completed: true,
      });
      toast.success(`Installed "${r.data.installed_template}" — added ${r.data.added.job_statuses} statuses + ${r.data.added.custom_fields} custom fields.`);
      setStep(4);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Install failed");
    } finally { setInstalling(false); }
  };

  // iter148 — Enter preview/adjust mode by fetching the template payload.
  const openPreview = async () => {
    if (!pickedTemplate) return;
    setBusy(true);
    try {
      // Fetch payload details for the picked template
      const r = await axios.get(`${API}/platform/templates/${pickedTemplate.id}`);
      const tpayload = r.data?.payload || {};
      setPreviewData(tpayload);
      // Default: all selected
      setSelectedStatuses(new Set((tpayload.job_statuses || []).map((s) => s.name).filter(Boolean)));
      setSelectedFields(new Set((tpayload.custom_fields || []).map((f) => f.field_key).filter(Boolean)));
      setPreviewMode(true);
    } catch (e) {
      toast.error("Couldn't load template preview");
    } finally { setBusy(false); }
  };

  const toggleStatusSel = (name) => setSelectedStatuses((s) => {
    const next = new Set(s); next.has(name) ? next.delete(name) : next.add(name); return next;
  });
  const toggleFieldSel = (key) => setSelectedFields((s) => {
    const next = new Set(s); next.has(key) ? next.delete(key) : next.add(key); return next;
  });

  if (!isAdmin) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !forceOpen) onClose?.(); }}>
      <DialogContent className="max-w-2xl" data-testid="onboarding-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" /> Welcome to Business Genie
          </DialogTitle>
          <DialogDescription>
            Step {step} of 4 — let&apos;s tune Business Genie to your business in 60 seconds.
            You can skip this and start with a blank slate any time.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <p>This quick wizard sets you up with starter <strong>job statuses</strong>, <strong>custom fields</strong>, <strong>quick actions</strong> and <strong>dashboard cards</strong> suited to your kind of business.</p>
            <p className="text-sm text-muted-foreground">Everything is editable afterwards. Nothing here locks you in.</p>
            <div className="rounded-lg border-2 border-dashed p-4 text-sm">
              <strong>Want to start blank instead?</strong> Click <em>Skip and start blank</em> — you can come back to this from <em>Settings → Onboarding</em>.
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div>
              <Label>What kind of business are you running?</Label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. HVAC repair, Mobile mechanic, Window cleaning, Wedding florist..."
                data-testid="onboarding-category-input" />
              <p className="text-xs text-muted-foreground mt-1">Free-text — anything works.</p>
            </div>
            <div>
              <Label>Which of these apply? (pick as many as you want)</Label>
              <div className="grid grid-cols-2 gap-2 mt-2" data-testid="onboarding-concepts">
                {allConcepts.map((c) => {
                  const isChecked = concepts.includes(c.key);
                  return (
                    <label
                      key={c.key}
                      onClick={() => toggleConcept(c.key)}
                      className={`flex items-center gap-2 cursor-pointer p-3 rounded-lg border-2 transition-all select-none ${
                        isChecked
                          ? "border-emerald-500 bg-emerald-500/15 shadow-sm ring-2 ring-emerald-500/30"
                          : "border-border hover:bg-muted/40 hover:border-muted-foreground/40 active:bg-muted/60"
                      }`}
                      data-testid={`onboarding-concept-${c.key}`}
                    >
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => toggleConcept(c.key)}
                        onClick={(e) => e.stopPropagation()}
                        className={isChecked ? "border-emerald-600 data-[state=checked]:bg-emerald-600" : ""}
                      />
                      <span className={`text-sm ${isChecked ? "font-semibold text-foreground" : "text-foreground/90"}`}>
                        {c.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {step === 3 && !previewMode && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Pick a starter pack to install. The closer to the top, the better the match for what you told us.</p>
            <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-3 text-xs" data-testid="onboarding-blank-hint">
              <p className="font-semibold text-amber-800 dark:text-amber-300 mb-0.5">Don&apos;t see your business type?</p>
              <p className="text-foreground/80">Start with a <strong>blank slate</strong> (click <em>Finish without installing</em> below), or pick the closest match and tweak it in <strong>Customize Business</strong>. Your customized layout can then be saved as a preset — future users with the same business type will see it in this list too.</p>
            </div>
            <div className="space-y-2 max-h-96 overflow-auto" data-testid="onboarding-template-list">
              {matchedTemplates.map((t) => {
                const Icon = ICONS[t.icon_key] || Sparkles;
                const isPicked = pickedTemplate?.id === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setPickedTemplate(t)}
                    className={`w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-colors ${isPicked ? "border-primary bg-primary/5" : "hover:bg-muted/40"}`}
                    data-testid={`onboarding-template-${t.id}`}
                  >
                    <div className={`shrink-0 p-2 rounded-md ${isPicked ? "bg-primary/15" : "bg-muted"}`}>
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-semibold">{t.name}</span>
                        {t._score > 0 && <Badge variant="default" className="text-[10px]">Match {t._score}</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{t.description}</p>
                      <div className="flex flex-wrap gap-1 mt-2 text-[10px]">
                        {t.summary?.job_statuses > 0 && <Badge variant="outline">{t.summary.job_statuses} statuses</Badge>}
                        {t.summary?.custom_fields > 0 && <Badge variant="outline">{t.summary.custom_fields} fields</Badge>}
                        {t.summary?.quick_actions > 0 && <Badge variant="outline">{t.summary.quick_actions} quick actions</Badge>}
                        {t.summary?.stat_cards > 0 && <Badge variant="outline">{t.summary.stat_cards} dashboard cards</Badge>}
                      </div>
                    </div>
                  </button>
                );
              })}
              {templates.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">No templates available right now. You can finish without installing one.</p>
              )}
            </div>
          </div>
        )}

        {/* iter148 — Adjust before installing */}
        {step === 3 && previewMode && (
          <div className="space-y-3" data-testid="onboarding-preview-mode">
            <div className="rounded-md bg-primary/10 border-2 border-primary/30 p-3">
              <p className="font-semibold text-sm">Review &amp; adjust: <span className="text-primary">{pickedTemplate?.name}</span></p>
              <p className="text-xs text-muted-foreground mt-0.5">Uncheck any item you don&apos;t want. Only checked items will be installed — everything else stays out of your business.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[420px] overflow-auto">
              <div className="border-2 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold">Job Statuses ({selectedStatuses.size}/{(previewData?.job_statuses || []).length})</p>
                  <button
                    type="button"
                    className="text-[11px] underline text-primary"
                    onClick={() => {
                      const all = (previewData?.job_statuses || []).map((s) => s.name);
                      setSelectedStatuses(selectedStatuses.size === all.length ? new Set() : new Set(all));
                    }}
                    data-testid="preview-toggle-all-statuses"
                  >
                    {selectedStatuses.size === (previewData?.job_statuses || []).length ? "Deselect all" : "Select all"}
                  </button>
                </div>
                <div className="space-y-1.5">
                  {(previewData?.job_statuses || []).map((s) => {
                    const on = selectedStatuses.has(s.name);
                    return (
                      <label
                        key={s.name}
                        onClick={() => toggleStatusSel(s.name)}
                        className={`flex items-center gap-2 cursor-pointer p-2 rounded border-2 transition-all ${on ? "border-emerald-500 bg-emerald-500/10" : "border-border hover:bg-muted/40"}`}
                        data-testid={`preview-status-${s.name.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <Checkbox checked={on} onCheckedChange={() => toggleStatusSel(s.name)} onClick={(e) => e.stopPropagation()} />
                        <span className="inline-block w-3 h-3 rounded-full shrink-0" style={{ background: s.color || "#64748b" }} />
                        <span className={`text-sm ${on ? "font-medium" : ""}`}>{s.name}</span>
                      </label>
                    );
                  })}
                  {(previewData?.job_statuses || []).length === 0 && (
                    <p className="text-xs text-muted-foreground italic">No statuses in this template.</p>
                  )}
                </div>
              </div>

              <div className="border-2 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold">Custom Fields ({selectedFields.size}/{(previewData?.custom_fields || []).length})</p>
                  <button
                    type="button"
                    className="text-[11px] underline text-primary"
                    onClick={() => {
                      const all = (previewData?.custom_fields || []).map((f) => f.field_key);
                      setSelectedFields(selectedFields.size === all.length ? new Set() : new Set(all));
                    }}
                    data-testid="preview-toggle-all-fields"
                  >
                    {selectedFields.size === (previewData?.custom_fields || []).length ? "Deselect all" : "Select all"}
                  </button>
                </div>
                <div className="space-y-1.5">
                  {(previewData?.custom_fields || []).map((f) => {
                    const on = selectedFields.has(f.field_key);
                    return (
                      <label
                        key={f.field_key}
                        onClick={() => toggleFieldSel(f.field_key)}
                        className={`flex items-start gap-2 cursor-pointer p-2 rounded border-2 transition-all ${on ? "border-emerald-500 bg-emerald-500/10" : "border-border hover:bg-muted/40"}`}
                        data-testid={`preview-field-${f.field_key}`}
                      >
                        <Checkbox checked={on} onCheckedChange={() => toggleFieldSel(f.field_key)} onClick={(e) => e.stopPropagation()} />
                        <div className="min-w-0">
                          <p className={`text-sm ${on ? "font-medium" : ""}`}>{f.label || f.field_key}</p>
                          <p className="text-[11px] text-muted-foreground">{f.entity_type} · {f.field_type}</p>
                        </div>
                      </label>
                    );
                  })}
                  {(previewData?.custom_fields || []).length === 0 && (
                    <p className="text-xs text-muted-foreground italic">No custom fields in this template.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="py-4 space-y-4" data-testid="onboarding-step-4">
            <div className="text-center space-y-2">
              <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-500" />
              <h3 className="font-heading text-xl font-semibold">You&apos;re all set!</h3>
              <p className="text-sm text-muted-foreground">Here&apos;s what the guided tour saved for your business:</p>
            </div>

            {/* Compare panel — the guided-tour choices summary */}
            <div className="rounded-lg border-2 border-emerald-500/30 bg-emerald-500/5 p-4 space-y-3" data-testid="onboarding-compare-panel">
              <div>
                <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">Business Type</p>
                <p className="text-sm">{category || <span className="italic text-muted-foreground">Not set</span>}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">Focus Areas ({concepts.length})</p>
                <div className="flex flex-wrap gap-1">
                  {concepts.length === 0 ? (
                    <span className="text-xs italic text-muted-foreground">None picked</span>
                  ) : concepts.map((k) => {
                    const c = allConcepts.find((x) => x.key === k);
                    return <Badge key={k} variant="secondary" className="text-xs">{c?.label || k}</Badge>;
                  })}
                </div>
              </div>
              {pickedTemplate && (
                <div>
                  <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">Starter Template</p>
                  <p className="text-sm">{pickedTemplate.name}</p>
                </div>
              )}
            </div>

            <div className="rounded-lg border p-4 space-y-2 bg-muted/20">
              <p className="text-sm font-semibold flex items-center gap-2">
                <Settings2 className="h-4 w-4 text-primary" /> Want to customize further?
              </p>
              <p className="text-xs text-muted-foreground">
                Open the <strong>Customize Business</strong> page to fine-tune your job statuses, custom fields,
                dashboard layout and compare them against these guided choices.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { onClose?.(); navigate("/customization"); }}
                className="mt-1 gap-2"
                data-testid="onboarding-open-customize-btn"
              >
                Open Customize Business <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="flex items-center justify-between sm:justify-between gap-2 flex-wrap">
          <div>
            {step === 1 && (
              <Button variant="ghost" onClick={handleSkip} disabled={busy} data-testid="onboarding-skip-btn">
                <SkipForward className="h-4 w-4 mr-1" /> Skip and start blank
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step > 1 && step < 4 && (
              <Button variant="outline" onClick={() => setStep(step - 1)} disabled={installing || busy}>
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
            {step < 3 && (
              <Button onClick={() => setStep(step + 1)} data-testid={`onboarding-next-${step}`}>
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {step === 3 && !previewMode && (
              <>
                {pickedTemplate ? (
                  <>
                    <Button variant="outline" onClick={openPreview} disabled={busy || installing} data-testid="onboarding-preview-btn">
                      {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                      Preview &amp; Adjust
                    </Button>
                    <Button onClick={handleInstall} disabled={installing} data-testid="onboarding-install-btn">
                      {installing && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                      Install all &amp; finish
                    </Button>
                  </>
                ) : (
                  <Button onClick={handleFinishWithoutInstall} variant="outline" disabled={busy} data-testid="onboarding-finish-noinstall-btn">
                    Finish without installing
                  </Button>
                )}
              </>
            )}
            {step === 3 && previewMode && (
              <>
                <Button variant="outline" onClick={() => setPreviewMode(false)} disabled={installing} data-testid="onboarding-preview-cancel-btn">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back to templates
                </Button>
                <Button onClick={handleInstall} disabled={installing || (selectedStatuses.size === 0 && selectedFields.size === 0)} data-testid="onboarding-install-selected-btn">
                  {installing && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                  Install {selectedStatuses.size + selectedFields.size} selected
                </Button>
              </>
            )}
            {step === 4 && (
              <Button onClick={onClose} data-testid="onboarding-done-btn">Done</Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
